// ============================================================================
// Quote Brain projects in the browser (docs/quote-brain/DESIGN.md 2.11, 2.12;
// slice WRITE).
// ----------------------------------------------------------------------------
// A brain project's quote is written by the server run: the backend stores one
// list in Stage 4 and Stage 5 and stamps pipeline_version 'quote-brain-v1'.
// No single function sits upstream of every old writer, so each one (W1 to
// W7) asks isBrainProject() and refuses, or prices through pricing-core (W4).
// The server's no-downgrade rule on pipeline_version is the backstop (W8).
//
// What the check reads, keyed by project id so a closure from another project
// can never answer for this one:
//   - the pipelineVersion of the record the tab loaded (no React state held it
//     before this slice; use-project-persistence.ts notes it on every load);
//   - a Quote Brain run that is live on the project (the run banner marks it),
//     so the old pipeline cannot write over a first run while it works;
//   - rows the brain wrote, for a writer handed a scope directly.
// ============================================================================

export const QUOTE_BRAIN_PIPELINE_VERSION = 'quote-brain-v1';
export const QUOTE_BRAIN_ROW_GENERATOR = 'quote-brain-v1';
export const DEFAULT_PIPELINE_VERSION = 'wms-first-v1';

const loadedPipelineVersion = new Map<string, string | null>();
// Project id -> the id of the run live on it ('' when the banner did not say).
const liveRuns = new Map<string, string>();

const key = (projectId: unknown): string => (projectId == null ? '' : String(projectId));

export function isBrainPipelineVersion(value: unknown): boolean {
    return value === QUOTE_BRAIN_PIPELINE_VERSION;
}

/** Called on every project load with the loaded record's pipelineVersion. */
export function noteLoadedProjectPipeline(projectId: unknown, pipelineVersion: unknown): void {
    const id = key(projectId);
    if (!id) return;
    loadedPipelineVersion.set(id, typeof pipelineVersion === 'string' ? pipelineVersion : null);
}

/** The version a browser save sends: never downgrades a brain project. */
export function pipelineVersionForSave(projectId: unknown): string {
    return isBrainPipelineVersion(loadedPipelineVersion.get(key(projectId)))
        ? QUOTE_BRAIN_PIPELINE_VERSION
        : DEFAULT_PIPELINE_VERSION;
}

/**
 * The run banner marks a live run, and clears the mark when the run stops.
 * While a run is live on a job the Quote Brain is writing its quote, so Stage
 * 4, Stage 5 and the Assistant leave the items alone (Peter's O1,
 * 2026-10-28). Screens follow the mark through subscribeQuoteBrainJobRules.
 */
export function markQuoteBrainRunLive(projectId: unknown, live: boolean, runId: unknown = ''): void {
    const id = key(projectId);
    if (!id) return;
    const run = runId == null ? '' : String(runId);
    if (live) {
        if (liveRuns.has(id) && (!run || liveRuns.get(id) === run)) return;
        liveRuns.set(id, run || liveRuns.get(id) || '');
    } else {
        if (!liveRuns.has(id)) return;
        liveRuns.delete(id);
    }
    rulesChanged();
}

export function isQuoteBrainRunLive(projectId: unknown): boolean {
    return liveRuns.has(key(projectId));
}

/** The run live on this job, for a Stop button away from the banner. Null when none is live or its id is not known. */
export function liveQuoteBrainRunId(projectId: unknown): string | null {
    return liveRuns.get(key(projectId)) || null;
}

/**
 * The setScope App hands its screens (Stage 4, Stage 5, the Assistant): it does nothing while the Quote Brain is writing the
 * open job's quote (O1, review V3), so no screen, card or late re-estimate changes the items meanwhile. App's own loads and
 * server adoptions keep using setScope itself. Reads the open job through a getter, so one stable setter serves every job.
 */
export function scopeSetterRefusingWhileQuoteBrainWrites<T>(setScope: (next: T) => void, getProjectId: () => unknown): (next: T) => void {
    return (next: T) => {
        if (isQuoteBrainRunLive(getProjectId())) return;
        setScope(next);
    };
}

// Project id -> the run whose quote the Quote Brain wrote on the job while this tab held an older version, until the tab
// loads it (review round 2, W1 to W3). A stale-save conflict on that job then offers only to load the Quote Brain's quote:
// replacing the saved version would erase it. The run banner sets and clears the mark.
const unloadedWrites = new Map<string, string>();

export function markQuoteBrainWriteUnloaded(projectId: unknown, runId: string | null): void {
    const id = key(projectId);
    if (!id || (unloadedWrites.get(id) || null) === (runId || null)) return;
    if (runId) unloadedWrites.set(id, runId); else unloadedWrites.delete(id);
    rulesChanged();
}

/** The run whose written quote this tab has not loaded yet on this job, or null. */
export function quoteBrainWriteUnloaded(projectId: unknown): string | null {
    return unloadedWrites.get(key(projectId)) || null;
}

/** What the update notice says when the saved version holds the Quote Brain's quote and this tab has not loaded it. */
export const QUOTE_BRAIN_WROTE_WHILE_EDITING = 'Your quote was written while you were editing. Load it to see it. Changes from the last few seconds that were not saved yet need typing again.';

/** What a refused item change on a job the Quote Brain is writing tells the builder. */
export const QUOTE_BRAIN_WRITING_MESSAGE = 'QuoteMaker is writing this quote now, so its items cannot be changed. Wait for it to finish, or stop it.';

export function isBrainScope(scope: unknown): boolean {
    return Array.isArray(scope) && scope.some((row: any) => row?.generatedBy === QUOTE_BRAIN_ROW_GENERATOR);
}

/**
 * True when this quote is one the Quote Brain wrote: its meta carries the run record, or its rows are the brain's. Read from the
 * quote alone, so the stage pills and the quote checks answer the same way for a saved quote as for the open one.
 */
export function isQuoteBrainQuoteData(quoteData: unknown): boolean {
    if (!quoteData || typeof quoteData !== 'object') return false;
    const meta = (quoteData as any).meta;
    if (meta && typeof meta === 'object') {
        if (meta.quoteBrain && typeof meta.quoteBrain === 'object') return true;
        if (meta.generatedBy === QUOTE_BRAIN_PIPELINE_VERSION) return true;
    }
    return isBrainScope((quoteData as any).scope);
}

/**
 * True when the Quote Brain owns this project's quote: the loaded record is a
 * brain project, a brain run is live on it, or the rows handed in are the
 * brain's.
 */
export function isBrainProject(projectId: unknown, { scope }: { scope?: unknown } = {}): boolean {
    const id = key(projectId);
    if (id && isBrainPipelineVersion(loadedPipelineVersion.get(id))) return true;
    if (id && liveRuns.has(id)) return true;
    return isBrainScope(scope);
}

// W0: under a business whose Quote Brain is on, a new job is the Quote
// Brain's to write, so the old pipeline does not start on it by itself after
// the summary (the Risk countdown, an automatic Risk run, the Quote Run's
// carry-on). The summary stays. A builder's own click still runs the old
// pipeline, so nothing is ever stuck. What the rule reads:
//   - the business's mode, from the run banner's status read;
//   - jobs put back on the old pipeline ("Use the old pipeline"), from the
//     banner's read of the job's runs, so a reload keeps the choice;
//   - the job's own content: no items, no Work Method, no Risk analysis.
let businessMode: 'off' | 'shadow' | 'on' | null = null;
// The status read's `ready`: the brain can write for this business. Null until read, false when the read failed.
let businessReady: boolean | null = null;
// Project id -> what the run banner last read about the server starting its run (the runs list's autoStart.state).
const autoStarts = new Map<string, string>();
const releasedJobs = new Set<string>();
let rulesVersion = 0;
const rulesListeners = new Set<() => void>();

function rulesChanged(): void {
    rulesVersion += 1;
    rulesListeners.forEach((fn) => fn());
}

export function noteQuoteBrainBusinessMode(mode: unknown, ready?: unknown): void {
    const next = mode === 'off' || mode === 'shadow' || mode === 'on' ? mode : null;
    const nextReady = typeof ready === 'boolean' ? ready : businessReady;
    if (next === businessMode && nextReady === businessReady) return;
    businessMode = next;
    businessReady = nextReady;
    rulesChanged();
}

export function getQuoteBrainBusinessMode(): 'off' | 'shadow' | 'on' | null {
    return businessMode;
}

/**
 * True when the Quote Brain writes this business's new jobs: its mode is on and it can write for the business. New
 * project then skips the Full AI / Fast-Track chooser (Peter's O1, 2026-10-31: under the Quote Brain the choice "doesn't
 * really make sense"), and writing the scope by hand is a choice on the job instead.
 */
export function quoteBrainWritesNewJobs(): boolean {
    return businessMode === 'on' && businessReady === true;
}

/**
 * True when the business's status says QuoteMaker cannot write its jobs: switched off, or a status that says it cannot
 * write for the business (no rates, a licence state the method has no rules for). False while the status is unread.
 */
export function quoteBrainCannotWriteForBusiness(): boolean {
    return businessMode === 'off' || (businessMode === 'on' && businessReady === false);
}

/** The run banner's last read of whether the server starts this job's run by itself; null when unknown. */
export function noteQuoteBrainAutoStart(projectId: unknown, state: string | null): void {
    const id = key(projectId);
    if (!id || (autoStarts.get(id) ?? null) === (state || null)) return;
    if (state) autoStarts.set(id, state); else autoStarts.delete(id);
    rulesChanged();
}

export function markQuoteBrainJobReleased(projectId: unknown, released = true): void {
    const id = key(projectId);
    if (!id || releasedJobs.has(id) === released) return;
    if (released) releasedJobs.add(id); else releasedJobs.delete(id);
    // The job is back on the old pipeline, so this tab stops treating it as a
    // brain job even before it loads the job again.
    if (released && isBrainPipelineVersion(loadedPipelineVersion.get(id))) loadedPipelineVersion.set(id, DEFAULT_PIPELINE_VERSION);
    rulesChanged();
}

/** For React (useSyncExternalStore): changes whenever the W0 rule's inputs do. */
export function subscribeQuoteBrainJobRules(fn: () => void): () => void {
    rulesListeners.add(fn);
    return () => { rulesListeners.delete(fn); };
}

export function getQuoteBrainJobRulesVersion(): number {
    return rulesVersion;
}

const hasContent = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value as object).length > 0;
    return !!value;
};

export function isQuoteBrainNewJob(
    projectId: unknown,
    { scope, wms, riskAnalysis }: { scope?: unknown; wms?: unknown; riskAnalysis?: unknown } = {},
): boolean {
    const id = key(projectId);
    if (businessMode !== 'on' || !id || releasedJobs.has(id)) return false;
    return !hasContent(scope) && !hasContent(wms) && !hasContent(riskAnalysis);
}

/**
 * What Stage 1's forward button does on a Quote Brain job (Peter, 2026-10-31: "why is there an Analyse button as well?"):
 *   'writing'  a run is live, or the run banner says the server starts this new job's run by itself ('expected' or
 *              'waiting', noteQuoteBrainAutoStart), so the button is not shown: the banner shows how far it is, and
 *              Stop once it writes;
 *   'written'  the Quote Brain wrote it, so the button opens the items (Stage 4) instead of running a Risk report the
 *              quote does not use;
 *   null       anything else, Analyse as before. On a new job Analyse is the only way into the old pipeline (the Risks
 *              pill stays locked until a report exists), so it stays whenever the banner promises no start: the status
 *              or the runs could not be read, the brain cannot write for this business, or the server's start failed.
 */
export function quoteBrainStage1Route(
    projectId: unknown,
    { scope, wms, riskAnalysis }: { scope?: unknown; wms?: unknown; riskAnalysis?: unknown } = {},
): 'writing' | 'written' | null {
    if (isQuoteBrainRunLive(projectId)) return 'writing';
    const promised = autoStarts.get(key(projectId));
    if (businessReady === true && (promised === 'expected' || promised === 'waiting')
        && isQuoteBrainNewJob(projectId, { scope, wms, riskAnalysis })) return 'writing';
    if (Array.isArray(scope) && scope.length > 0 && isBrainProject(projectId, { scope })) return 'written';
    return null;
}

// The stage pills on a Quote Brain job (lucid 017 O1): the brain writes no Risk report and no Work Method, so those two stages read
// "not run" until the job has one of its own. A Risk report can still be run on demand from Stage 2.
export const QUOTE_BRAIN_NOT_RUN_STAGES: Readonly<Record<number, string>> = Object.freeze({
    2: 'Risks: not run. QuoteMaker writes the quote without a Risk report. Open Risks to run one if you want it.',
    3: 'Method: not run. QuoteMaker writes the quote without a Work Method.',
});

export const QUOTE_BRAIN_NEW_JOB_MESSAGE = "The summary is saved. QuoteMaker writes this job's quote on the server.";

/** What a refused old writer tells the builder. */
export function quoteBrainRefusalMessage(action: string, projectId?: unknown): string {
    if (projectId != null && isQuoteBrainRunLive(projectId) && !isBrainPipelineVersion(loadedPipelineVersion.get(key(projectId)))) {
        return `QuoteMaker is writing this quote now, so ${action} is not available. Wait for it to finish, or stop it from the banner.`;
    }
    return `QuoteMaker wrote this quote, so ${action} is not available for it. Edit the items in Stage 4 or Stage 5, or write the quote again from Stage 1.`;
}

export class QuoteBrainProjectRefusal extends Error {
    code = 'QUOTE_BRAIN_PROJECT';
    retryable = false;
    constructor(message: string) {
        super(message);
        this.name = 'QuoteBrainProjectRefusal';
    }
}

/** For tests. */
export function __resetQuoteBrainProjectState(): void {
    loadedPipelineVersion.clear();
    liveRuns.clear();
    unloadedWrites.clear();
    releasedJobs.clear();
    businessMode = null;
    businessReady = null;
    autoStarts.clear();
    rulesChanged();
}
